/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { WarningRounded } from '@mui/icons-material';
import { Box, Button, FormControl, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { isNil, uniqBy } from 'lodash';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ManualDowntimePeriod } from '../../../shared/entities/manualDowntimePeriod';
import {
    selectAddedManualDowntimePeriodError,
    selectDeletedManualDowntimePeriodError,
    selectIsAddingManualDowntimePeriod,
    selectIsDeletingManualDowntimePeriod,
    selectIsRequestingManualDowntimePeriods,
    selectManualDowntimePeriods,
    selectRequestManualDowntimePeriodsError,
    selectTerminals
} from '../../../store';
import {
    addManualDowntimePeriod,
    clearManualDowntimePeriods,
    deleteManualDowntimePeriod,
    requestManualDowntimePeriods,
} from '../../../store/manualDowntimePeriods/actions';
import {
    DATA_USAGE_PAGE_MAP_ID,
    FLEET_MAP_ID,
    FTV_MAP_ID,
    PERFORMANCE_OUTLOOK_MAP_ID,
    PROVISIONING_PAGE_MAP_ID,
    TICKETS_PAGE_MAP_ID,
    setMapHidden,
} from '../../../store/maps/actions';
import { getTerminalsOnce } from '../../../store/terminals/actions';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../utils/StringUtils';
import Scrollbars from '../../common/layout/Scrollbars';
import appTheme from '../../common/theme/appTheme.json';
import ManualDowntimeDateInputs from './ManualDowntimeDateInputs';
import ManualDowntimeFeedbackContainer from './ManualDowntimeFeedbackContainer';
import ManualDowntimePeriodsTable from './ManualDowntimePeriodsTable';

const COMPONENT_WIDTH_PX = 600;

const ManualDowntimeContainer = () => {
  dayjs.extend(utc);

  // Local State
  const [selectedDatapathUuid, setSelectedDatapathUuid] = useState<string | undefined | null>();
  const [startDate, setStartDate] = useState<dayjs.Dayjs | undefined | null>();
  const [endDate, setEndDate] = useState<dayjs.Dayjs | undefined | null>();
  const [confirmManualDowntimeDeleteModalVisible, setConfirmManualDowntimeDeleteModalVisible] =
    useState<boolean>(false);
  const [manualDowntimePeriodToDelete, setManualDowntimePeriodToDelete] = useState<ManualDowntimePeriod | undefined>();
  const [manualDowntimeDeleteErroredSnakbarVisible, setManualDowntimeDeleteErroredSnakbarVisible] =
    useState<boolean>(false);
  const [manualDowntimeAddErroredSnakbarVisible, setManualDowntimeAddErroredSnakbarVisible] = useState<boolean>(false);
  const [manualDowntimePeriodsRerenderInputsKey, setManualDowntimePeriodsRerenderInputsKey] = useState<number>(0);
  // 3rd party hooks
  const dispatch = useDispatch();

  // Selectors
  const terminals = useSelector(selectTerminals, shallowEqual);
  const manualDowntimePeriods = useSelector(selectManualDowntimePeriods, shallowEqual);
  const isRequestingManualDowntimePeriods = useSelector(selectIsRequestingManualDowntimePeriods, shallowEqual);
  const requestManualDowntimePeriodsError = useSelector(selectRequestManualDowntimePeriodsError, shallowEqual);
  const isAddingManualDowntimePeriod = useSelector(selectIsAddingManualDowntimePeriod, shallowEqual);
  const addedManualDowntimePeriodError = useSelector(selectAddedManualDowntimePeriodError, shallowEqual);
  const isDeletingManualDowntimePeriod = useSelector(selectIsDeletingManualDowntimePeriod, shallowEqual);
  const deletedManualDowntimePeriodError = useSelector(selectDeletedManualDowntimePeriodError, shallowEqual);

  // Lifecycle Hooks
  useEffect(() => {
    dispatch(setMapHidden(FLEET_MAP_ID)(true));
    dispatch(setMapHidden(FTV_MAP_ID)(true));
    dispatch(setMapHidden(PROVISIONING_PAGE_MAP_ID)(true));
    dispatch(setMapHidden(TICKETS_PAGE_MAP_ID)(true));
    dispatch(setMapHidden(PERFORMANCE_OUTLOOK_MAP_ID)(true));
    dispatch(setMapHidden(DATA_USAGE_PAGE_MAP_ID)(true));
    dispatch(getTerminalsOnce());

    return () => {
      dispatch(setMapHidden(FLEET_MAP_ID)(false));
      dispatch(setMapHidden(FTV_MAP_ID)(false));
      dispatch(setMapHidden(PERFORMANCE_OUTLOOK_MAP_ID)(false));
    };
  }, [dispatch]);

  useEffect(() => {
    return () => {
      setConfirmManualDowntimeDeleteModalVisible(false);
      setManualDowntimeDeleteErroredSnakbarVisible(false);
      setManualDowntimeAddErroredSnakbarVisible(false);
    };
  }, []);

  useEffect(() => {
    if (addedManualDowntimePeriodError) {
      setManualDowntimeAddErroredSnakbarVisible(true);
    }
  }, [addedManualDowntimePeriodError]);

  useEffect(() => {
    if (deletedManualDowntimePeriodError) {
      setManualDowntimeDeleteErroredSnakbarVisible(true);
    }
  }, [deletedManualDowntimePeriodError]);

  useEffect(() => {
    return () => {
      setConfirmManualDowntimeDeleteModalVisible(false);
      dispatch(clearManualDowntimePeriods());
    };
  }, [dispatch]);

  useEffect(() => {
    const tailNumber = !isNil(selectedDatapathUuid) ? terminals?.[selectedDatapathUuid]?.aircraftTailNumber : undefined;
    if (!isNil(tailNumber)) {
      dispatch(requestManualDowntimePeriods(tailNumber));
    }
  }, [selectedDatapathUuid, terminals, dispatch]);
  const memoizedDateRangeInputs = useMemo(
    () => (
      <ManualDowntimeDateInputs
        startDateChangeHandler={(startDate: any) => {
          setStartDate(startDate);
        }}
        endDateChangeHandler={(endDate: any) => {
          setEndDate(endDate);
        }}
        manualDowntimePeriodsRerenderInputsKey={manualDowntimePeriodsRerenderInputsKey}
      ></ManualDowntimeDateInputs>
    ),
    [setStartDate, setEndDate, manualDowntimePeriodsRerenderInputsKey],
  );
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        height: 'calc(100vh - 52px)',
        backgroundColor: appTheme.secondaryBackgroundColor,
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            paddingY: '16px',
            display: 'flex',
            justifyContent: 'flex-start',
            backgroundColor: appTheme.primaryBackgroundColor,
          }}
        >
          <Typography
            sx={{
              fontFamily: appTheme.viasatFontFamilyHeavy,
              color: appTheme.primaryTextColor,
              fontWeight: 800,
              fontSize: '32px',
              paddingLeft: '16px',
              letterSpacing: '0.2px',
              lineHeight: '40px',
            }}
          >
            Manual Downtime Entry
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: '32px' }}>
          <Box
            sx={{
              width: `${COMPONENT_WIDTH_PX}px`,
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '28px',
              letterSpacing: '0px',
              color: '#ffffff',
            }}
          >
            Add Downtime
          </Box>
        </Box>
        <Box sx={{ paddingTop: '16px', display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: `${COMPONENT_WIDTH_PX}px` }}>
            <FormControl>
              <Box sx={{ color: '#9FAFBC', fontSize: '14px', lineHeight: '20px', height: '28px', fontWeight: 600 }}>
                Terminal
              </Box>

              <Autocomplete
                disablePortal
                disableClearable
                id="combo-box-demo"
                options={uniqBy(
                  Object.keys(terminals)
                    .map((datapathUuid: string) => terminals[datapathUuid])
                    .map((terminal: any) => ({
                      value: terminal.datapathUuid,
                      label: terminal.aircraftTailNumber,
                    })),
                  'label',
                )}
                onChange={(event: SyntheticEvent<Element, Event>, value: { value: string; label: string } | null) => {
                  if (!isNil(value?.value)) {
                    setSelectedDatapathUuid(value?.value!);
                    setManualDowntimePeriodsRerenderInputsKey(manualDowntimePeriodsRerenderInputsKey + 1);
                  } else {
                    setSelectedDatapathUuid(null);
                    dispatch(clearManualDowntimePeriods());
                  }
                }}
                sx={{
                  width: '286px',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#9FAFBC',
                    },
                    '&:hover fieldset': {
                      borderColor: 'white',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00C2DE',
                    },
                  },
                  input: { color: appTheme.primaryTextColor },
                  fieldset: {
                    borderColor: appTheme.mptFormLabelText,
                  },
                }}
                size="small"
                renderInput={params => <TextField {...params} />}
              />
            </FormControl>
          </Box>
        </Box>

        <Box>{memoizedDateRangeInputs}</Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: '16px' }}>
          <Box sx={{ width: `${COMPONENT_WIDTH_PX}px`, display: 'flex', justifyContent: 'end' }}>
            <Button
              sx={{
                width: '184px',
                height: '40px',
                borderRadius: '1000px',
                gap: '4px',
                color: '#202E39',
                backgroundColor: appTheme.mptLightBlue,
                fontFamily: 'Source Sans Pro',
                fontSize: '16px',
                fontWeight: '600px',
                lineHeight: '24px',
                letterSpacing: '0px',
                ':disabled': {
                  color: '#465967',
                  backgroundColor: '#677A89',
                  cursor: 'not-allowed',
                  pointerEvents: 'auto',
                  '&:hover': {
                    color: '#465967',
                    backgroundColor: '#677A89',
                    cursor: 'not-allowed',
                    pointerEvents: 'auto',
                  },
                },
                '&:hover': {
                  backgroundColor: appTheme.mptLightBlue,
                  svg: {
                    color: '#000000',
                  },
                },
              }}
              variant="contained"
              size="large"
              onClick={() => {
                const tailNumber = !isNil(selectedDatapathUuid)
                  ? terminals?.[selectedDatapathUuid]?.aircraftTailNumber
                  : undefined;
                if (!isNil(tailNumber) && !isNil(startDate) && !isNil(endDate)) {
                  dispatch(
                    addManualDowntimePeriod({
                      tailNumber: tailNumber,
                      startTimeEpochSecs: startDate.unix(),
                      endTimeEpochSecs: endDate.unix(),
                    }),
                  );
                }
                setStartDate(null);
                setEndDate(null);
                setManualDowntimePeriodsRerenderInputsKey(manualDowntimePeriodsRerenderInputsKey + 1);
              }}
              disabled={
                isNil(selectedDatapathUuid) ||
                isNil(startDate) ||
                isNil(endDate) ||
                isAddingManualDowntimePeriod ||
                (!isNil(startDate) && !isNil(endDate) && !dayjs(endDate).isAfter(startDate))
              }
            >
              Save Entry
            </Button>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: '32px' }}>
          <Box
            sx={{
              width: `${COMPONENT_WIDTH_PX}px`,
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '28px',
              letterSpacing: '0px',
              color: '#ffffff',
            }}
          >
            Terminal Downtime{' '}
            <Box sx={{ fontWeight: 400, display: 'inline-block' }}>
              {!isNil(selectedDatapathUuid) && !isNil(terminals?.[selectedDatapathUuid]?.aircraftTailNumber) ? (
                `(${terminals?.[selectedDatapathUuid]?.aircraftTailNumber})`
              ) : (
                <></>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: '16px' }}>
          <Box sx={{ width: `${COMPONENT_WIDTH_PX}px` }}>
            {manualDowntimePeriods?.length ? (
              <Scrollbars autoHeight autoHeightMax="calc(100vh - 501px)">
                <ManualDowntimePeriodsTable
                  onManualDowntimePeriodDeleteIconClicked={(manualDowntimePeriod: ManualDowntimePeriod) => {
                    setManualDowntimePeriodToDelete(manualDowntimePeriod);
                    setConfirmManualDowntimeDeleteModalVisible(true);
                  }}
                  manualDowntimePeriods={manualDowntimePeriods}
                ></ManualDowntimePeriodsTable>
              </Scrollbars>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {requestManualDowntimePeriodsError ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexDirection: 'column',
                      color: '#ffffff',
                    }}
                  >
                    <WarningRounded />
                    <Box>Error Retrieving Manual Downtime Periods</Box>
                    <Button
                      variant="text"
                      color="primary"
                      sx={{
                        marginTop: '12px',
                        zIndex: 5,
                        height: '40px',
                        width: '98px',
                        borderRadius: '1000px',
                        backgroundColor: '#18ECFC',
                        border: 'none',
                        color: '#202E39',
                        fontFamily: 'Source Sans Pro',
                        fontSize: '16px',
                        fontWeight: 600,
                        lineHeight: '24px',
                        letterSpacing: 0,
                        textAlign: 'center',
                        '&:hover': {
                          backgroundColor: '#18ECFC',
                        },
                      }}
                      onClick={() => {
                        const tailNumber = !isNil(selectedDatapathUuid)
                          ? terminals?.[selectedDatapathUuid]?.aircraftTailNumber
                          : undefined;
                        if (!isNil(tailNumber)) {
                          dispatch(requestManualDowntimePeriods(tailNumber));
                        }
                      }}
                    >
                      RETRY
                    </Button>
                  </Box>
                ) : !isRequestingManualDowntimePeriods && !isNil(selectedDatapathUuid) ? (
                  <Box sx={{ color: appTheme.primaryTextColor }}>No items to display </Box>
                ) : (
                  <></>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box>
        <Dialog
          open={confirmManualDowntimeDeleteModalVisible}
          onClose={() => {
            setConfirmManualDowntimeDeleteModalVisible(false);
          }}
        >
          <DialogTitle
            sx={{
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '28px',
              letterSpacing: '0.2px',
              width: '648px',
            }}
          >
            Confirm deletion
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: appTheme.primaryTextColor }}>
              <Box
                sx={{
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '24px',
                  letterSpacing: 0,
                  paddingBottom: '16px',
                }}
              >
                Delete this manual downtime entry?
              </Box>
              <Box sx={{ display: 'flex', paddingLeft: '24px' }}>
                <Box sx={{ flexGrow: 1, fontWeight: 'bold' }}>Tail:</Box>
                <Box sx={{}}>
                  {manualDowntimePeriodToDelete ? manualDowntimePeriodToDelete.tailNumber : EMPTY_FIELD_STRING_PLACEHOLDER}
                </Box>
              </Box>
            </DialogContentText>
            <DialogContentText sx={{ color: appTheme.primaryTextColor }}>
              <Box sx={{ display: 'flex', paddingLeft: '24px' }}>
                <Box sx={{ flexGrow: 1, fontWeight: 'bold' }}>Downtime Period:</Box>
                <Box>
                  <Box sx={{ display: 'inline-block' }}>
                    {manualDowntimePeriodToDelete
                      ? dayjs
                          .unix(manualDowntimePeriodToDelete?.startTimeEpochSecs)
                          .utc()
                          .format('DDMMMYYYY HH:mm')
                          .toUpperCase()
                          .concat('Z')
                      : EMPTY_FIELD_STRING_PLACEHOLDER}
                  </Box>{' '}
                  -{' '}
                  <Box sx={{ display: 'inline-block' }}>
                    {manualDowntimePeriodToDelete
                      ? dayjs
                          .unix(manualDowntimePeriodToDelete?.endTimeEpochSecs)
                          .utc()
                          .format(`DDMMMYYYY HH:mm`)
                          .toUpperCase()
                          .concat('Z')
                      : EMPTY_FIELD_STRING_PLACEHOLDER}
                  </Box>
                </Box>
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              sx={{
                height: '40px',
                width: '98px',
                borderRadius: '1000px',
                backgroundColor: '#202E39',
                color: '#ffffff',
                fontFamily: 'Source Sans Pro',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '24px',
                letterSpacing: 0,
                textAlign: 'center',
                '&:hover': {
                  backgroundColor: '#202E39',
                },
              }}
              onClick={() => {
                setManualDowntimePeriodToDelete(undefined);
                setConfirmManualDowntimeDeleteModalVisible(false);
              }}
            >
              Cancel
            </Button>
            <Button
              sx={{
                height: '40px',
                width: '98px',
                borderRadius: '1000px',
                backgroundColor: '#18ECFC',
                border: 'none',
                color: '#202E39',
                fontFamily: 'Source Sans Pro',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '24px',
                letterSpacing: 0,
                textAlign: 'center',
                '&:hover': {
                  backgroundColor: '#18ECFC',
                },
              }}
              onClick={() => {
                setConfirmManualDowntimeDeleteModalVisible(false);
                if (manualDowntimePeriodToDelete?.id) {
                  dispatch(deleteManualDowntimePeriod(manualDowntimePeriodToDelete?.id));
                }
                setManualDowntimePeriodToDelete(undefined);
              }}
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      <ManualDowntimeFeedbackContainer
        isAddingManualDowntimePeriod={isAddingManualDowntimePeriod}
        isDeletingManualDowntimePeriod={isDeletingManualDowntimePeriod}
        isRequestingManualDowntimePeriods={isRequestingManualDowntimePeriods}
        manualDowntimeAddErroredSnackbarVisible={manualDowntimeAddErroredSnakbarVisible}
        manualDowntimeDeleteErroredSnackbarVisible={manualDowntimeDeleteErroredSnakbarVisible}
        onCloseManualDowntimeAddErroredSnackbar={() => {
          setManualDowntimeAddErroredSnakbarVisible(false);
        }}
        onCloseManualDowntimeDeleteErroredSnackbar={() => {
          setManualDowntimeDeleteErroredSnakbarVisible(false);
        }}
      ></ManualDowntimeFeedbackContainer>
    </Box>
  );
};
export default ManualDowntimeContainer;
